import { z } from 'zod';

import { entityIdSchema, isoDateTimeSchema, moneySchema } from './common.js';

export const discountTypeSchema = z.enum(['none', 'fixed', 'percentage']);

export const priceLineSourceReferenceSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^(?:option:[^:\s]+|tarif:[^:\s]+(?::[^:\s]+)+)$/u);

export const commercialDiscountSchema = z
  .object({
    type: discountTypeSchema.default('none'),
    value: z.number().int().min(0).max(1_000_000_000).default(0),
    label: z.string().trim().max(240).default(''),
  })
  .superRefine((discount, context) => {
    if (discount.type === 'none' && discount.value !== 0) {
      context.addIssue({ code: 'custom', message: 'Une remise absente doit valoir zéro.' });
    }
    if (discount.type !== 'none' && (discount.value <= 0 || discount.label.length === 0)) {
      context.addIssue({
        code: 'custom',
        message: 'Une remise explicite exige une valeur positive et un motif.',
      });
    }
    if (discount.type === 'percentage' && discount.value > 10_000) {
      context.addIssue({ code: 'custom', message: 'Le pourcentage dépasse 100 %.' });
    }
  });

export const appliedPromoCodeSchema = z
  .object({
    id: entityIdSchema,
    usageId: entityIdSchema,
    code: z.string().trim().min(1).max(80),
    label: z.string().trim().min(1).max(240),
    kind: z.enum(['fixed', 'percentage', 'option']),
    value: z.number().int().positive().max(1_000_000_000),
    discountCents: moneySchema,
    appliedAt: isoDateTimeSchema,
  })
  .strict();

export const priceLineInputSchema = z.object({
  label: z.string().trim().min(1).max(240),
  detail: z.string().trim().max(2_000).default(''),
  sourceReference: priceLineSourceReferenceSchema.nullable().default(null),
  quantity: z.number().int().positive().max(10_000),
  unitPriceCents: moneySchema,
  taxRateBasisPoints: z.number().int().min(0).max(10_000).default(0),
});

export const priceLineSchema = priceLineInputSchema.extend({
  id: entityIdSchema,
});

export type CommercialDiscount = z.infer<typeof commercialDiscountSchema>;
export type AppliedPromoCode = z.infer<typeof appliedPromoCodeSchema>;
export type DiscountType = z.infer<typeof discountTypeSchema>;
export type PriceLine = z.infer<typeof priceLineSchema>;
export type PriceLineInput = z.infer<typeof priceLineInputSchema>;
